import { FormikConfig, FormikHelpers } from 'formik';
import { memo, useCallback, useMemo, useState } from 'react';
import * as uuid from 'uuid';
import { CreateModal } from '../../../components/Modal/CreateModal';
import { FormikCADFileInput } from '../../../components/formik/FormFileInput';
import { T } from '../../../translation/src';
import { useLayerOperations } from '../../../hooks/potree/useLayerOperations';
import { useUpload } from '../../../hooks/useUpload';
import { useParams } from 'react-router-dom';
import { useValidationTranslations } from '../../../hooks/useValidationTranslations';

interface FormValues {
  files?: FileList;
}

interface AddCADLayerModalProps {
  onClose: () => void;
  open?: boolean;
}
const AddCADLayerModal_: React.FC2<AddCADLayerModalProps> = ({ onClose, open }) => {
  const [loading, setLoading] = useState(false);
  const { createCadLayer } = useLayerOperations();
  const [, { uploadFiles }] = useUpload();
  const { projectId = '' } = useParams();
  const validationSchemaTranslations = useValidationTranslations();

  const onSuccess = useCallback(
    ({ helpers: { resetForm } }: { helpers: FormikHelpers<FormValues> }) => {
      onClose();
      // Because of transition
      setTimeout(() => {
        resetForm();
        setLoading(false);
      }, 250);
    },
    [onClose],
  );

  const onCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async ({ files }, helpers) => {
      setLoading(true);
      const cadLayerId = uuid.v4();
      const file = files?.[0];
      if (!file) {
        helpers.setFieldError('files', validationSchemaTranslations.isRequired);
        setLoading(false);
        return;
      }
      const [{ name }] = await uploadFiles({ files: [file], projectId });
      await createCadLayer({ cadLayerId, name: 'CAD layer', fileName: name });
      onSuccess({ helpers });
    },
    [createCadLayer, onSuccess, projectId, uploadFiles, validationSchemaTranslations.isRequired],
  );

  const formik: FormikConfig<FormValues> = useMemo(() => ({ initialValues: {}, onSubmit }), [onSubmit]);

  return (
    <CreateModal title={<T _str="Add a new CAD layer" swc />} createButtonTitle={<T _str="upload" swc />} formik={formik} onCancel={onCancel} open={open} isSubmitting={loading} alwaysCancellable>
      <FormikCADFileInput name="files" />
    </CreateModal>
  );
};

export const AddCADLayerModal = memo(AddCADLayerModal_);
